/**
 * React Hook for Offline Status
 * Provides real-time offline/online status and sync information
 */

import { useState, useEffect, useCallback } from 'react';
import offlineManager from './offlineManager';
import db from './db';

/**
 * Custom hook to manage offline status and sync
 */
export const useOfflineStatus = () => {
  const [isOnline, setIsOnline] = useState(offlineManager.isOnline);
  const [isSyncing, setIsSyncing] = useState(false);
  const [syncFailed, setSyncFailed] = useState(false);
  const [pendingCount, setPendingCount] = useState(0);
  const [syncProgress, setSyncProgress] = useState(null);
  const [conflictsCount, setConflictsCount] = useState(0);

  /**
   * Update pending count
   */
  const updatePendingCount = useCallback(async () => {
    try {
      const count = await offlineManager.getPendingCount();
      setPendingCount(count);
    } catch (error) {
      console.error('Error updating pending count:', error);
    }
  }, []);

  /**
   * Update conflicts count
   */
  const updateConflictsCount = useCallback(async () => {
    try {
      const conflicts = await db.getUnresolvedConflicts();
      setConflictsCount(conflicts.length);
    } catch (error) {
      console.error('Error updating conflicts count:', error);
    }
  }, []);

  /**
   * Manual sync trigger
   */
  const triggerSync = useCallback(async () => {
    try {
      setIsSyncing(true);
      await offlineManager.manualSync();
      await updatePendingCount();
      await updateConflictsCount();
    } catch (error) {
      console.error('Error during manual sync:', error);
      throw error;
    } finally {
      setIsSyncing(false);
    }
  }, [updatePendingCount, updateConflictsCount]);

  /**
   * Get sync status
   */
  const getSyncStatus = useCallback(async () => {
    try {
      return await offlineManager.getSyncStatus();
    } catch (error) {
      console.error('Error getting sync status:', error);
      return null;
    }
  }, []);

  /**
   * Clear old cache
   */
  const clearOldCache = useCallback(async (daysToKeep = 7) => {
    try {
      await offlineManager.clearOldCache(daysToKeep);
      await updatePendingCount();
    } catch (error) {
      console.error('Error clearing old cache:', error);
    }
  }, [updatePendingCount]);

  /**
   * Subscribe to offline manager events
   */
  useEffect(() => {
    const unsubscribe = offlineManager.subscribe((data) => {
      // Update online status
      if (data.online !== undefined) {
        setIsOnline(data.online);
        
        // Auto-sync is already handled by offlineManager.initializeListeners()
        // Just reset syncFailed when going online - the autoSyncComplete event
        // will arrive via the subscriber when sync finishes
        if (data.online) {
          setSyncFailed(false);
          setIsSyncing(true); // Show syncing indicator while offlineManager syncs
        }
      }

      // Sync failed
      if (data.syncFailed) {
        setSyncFailed(true);
      }

      // Update pending count
      if (data.pendingCount !== undefined) {
        setPendingCount(data.pendingCount);
      }

      // Update sync progress
      if (data.syncProgress) {
        setSyncProgress(data.syncProgress);
        setIsSyncing(true);
      }

      // Sync complete
      if (data.syncComplete) {
        setIsSyncing(false);
        setSyncProgress(null);
        // Set syncFailed based on success
        if (data.allSuccess === false) {
          setSyncFailed(true);
        } else {
          setSyncFailed(false);
        }
        updatePendingCount();
        updateConflictsCount();
      }

      // Auto-sync complete - trigger UI refresh
      if (data.autoSyncComplete) {
        console.log('🔄 Auto-sync complete - triggering UI refresh');
        setIsSyncing(false);
        setSyncFailed(false);
        updatePendingCount();
        updateConflictsCount();
        // Dispatch custom event for components to refresh their data
        window.dispatchEvent(new CustomEvent('offlineSyncComplete'));
      }

      // Transaction queued
      if (data.transactionQueued) {
        updatePendingCount();
      }
    });

    // Initial data load
    updatePendingCount();
    updateConflictsCount();

    return unsubscribe;
  }, [updatePendingCount, updateConflictsCount]);

  return {
    isOnline,
    isSyncing,
    syncFailed,
    pendingCount,
    syncProgress,
    conflictsCount,
    triggerSync,
    getSyncStatus,
    clearOldCache
  };
};

/**
 * Hook for database statistics
 */
export const useOfflineStats = () => {
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);

  const refreshStats = useCallback(async () => {
    try {
      setLoading(true);
      const dbStats = await db.getStats();
      setStats(dbStats);
    } catch (error) {
      console.error('Error loading stats:', error);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    refreshStats();
    
    // Refresh stats every 30 seconds
    const interval = setInterval(refreshStats, 30000);
    
    return () => clearInterval(interval);
  }, [refreshStats]);

  return {
    stats,
    loading,
    refreshStats
  };
};

export default useOfflineStatus;
